import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { leaveRequestApi } from "../../../api/leaveRequestApi";
import { REACT_APP_API_URL } from "../../../env";

const LeaveRequestList = ({ refreshTrigger }) => {
  const { hrmsUser } = useSelector((state) => state.auth);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [submittingCancel, setSubmittingCancel] = useState(false);

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      const response = await leaveRequestApi.getLeaveRequests(
        hrmsUser.employee_id
      );

      if (response.success) {
        const formattedData = response.data.map((item) => {

          let cancellationStatus = null;

          if (item.Archived) {
            cancellationStatus = "approved";
          }
          else if (item.CancellationAdminRemarks && !item.Archived) {
            cancellationStatus = "rejected";
          }
          else if (item.CancellationRequested && !item.Archived) {
            cancellationStatus = "pending";
          }

          return {
            id: item.LeaveID,
            employee_id: item.EmployeeID,
            applying_to: item.ApplyingTo || item.applying_to || "-",
            leave_from: item.StartDate,
            leave_to: item.EndDate,
            leave_type: item.LeaveType,
            total_days: item.TotalDays,
            reason: item.Reason,
            status: item.Status ? item.Status.toLowerCase() : "pending",
            created_at: item.CreatedDate,
            attachment: item.AttachmentPath || null,
            remarks: item.ManagerRemarks || null,

            cancellation_requested:
              item.CancellationRequested ||
              item.Archived ||
              !!item.CancellationAdminRemarks,
            cancellation_requested_date: item.CancellationRequestedDate || null,
            cancellation_status: cancellationStatus,
            cancellation_reason: item.CancellationReason || null,
            cancellation_admin_remarks: item.CancellationAdminRemarks || null
          };
        });

        const sortedData = formattedData.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setLeaveRequests(sortedData);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  };

  const submitCancellationRequest = async () => {
    if (!cancelReason.trim()) {
      alert("Cancellation reason is required.");
      return;
    }

    try {
      setSubmittingCancel(true);

      const response = await leaveRequestApi.requestCancellation(
        selectedLeaveId,
        { reason: cancelReason.trim() }
      );

      alert(response.message || "Cancellation requested successfully.");

      setShowCancelModal(false);
      setSelectedLeaveId(null);
      setCancelReason("");

      fetchLeaveRequests();

    } catch (err) {
      alert(err?.message || "Failed to request cancellation.");
    } finally {
      setSubmittingCancel(false);
    }
  };

  useEffect(() => {
    fetchLeaveRequests();
  }, [refreshTrigger]);

  const handleDeleteRequest = async (requestId) => {
    if (
      !window.confirm("Are you sure you want to delete this leave request?")
    ) {
      return;
    }

    try {
      const response = await leaveRequestApi.deleteLeaveRequest(requestId);
      if (response.success) {
        setLeaveRequests((prev) => prev.filter((req) => req.id !== requestId));
      }
    } catch (err) {
      setError(err.message || "Failed to delete leave request");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: "bg-warning text-dark",
      approved: "bg-success",
      rejected: "bg-danger",
      cancelled: "bg-secondary",
    };
    const displayText = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";

    return (
      <span className={`badge ${statusClasses[status] || "bg-secondary"}`}>
        {displayText}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  if (loading) {
    return (
      <div className="card leave-surface-card">
        <div className="card-body text-center leave-surface-body">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading leave requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card leave-surface-card">
      <div className="card-header leave-surface-header">
        <h5 className="card-title mb-0">My Leave Requests</h5>
      </div>
      <div className="card-body leave-surface-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {leaveRequests.length === 0 ? (
          <div className="text-center py-5 leave-empty-state">
            <div className="mb-3">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className="text-muted"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <h6 className="text-muted">No leave requests found</h6>
            <p className="text-muted">
              You haven't created any leave requests yet.
            </p>
          </div>
        ) : (
          <div className="leave-table-shell">
            <table className="table table-hover align-middle mb-0 leave-table">
              <thead>
                <tr>
                  <th scope="col">Applied Date</th>
                  <th scope="col">Applied To</th>
                  <th scope="col">Leave Type</th>
                  <th scope="col">Start Date</th>
                  <th scope="col">End Date</th>
                  <th scope="col">Total Days</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Attachment</th>
                  <th scope="col">Status</th>
                  <th scope="col">Admin Remarks</th>
                  <th scope="col">Actions</th>
                  <th scope="col">Cancellation</th>
                </tr>
              </thead>

              <tbody>
                {leaveRequests.map((request) => (
                  <tr key={request.id}>

                    <td>
                      <small className="text-muted">
                        {formatDate(request.created_at)}
                      </small>
                    </td>

                    <td><small>{request.applying_to || "-"}</small></td>

                    <td>
                      <span className="badge bg-primary text-uppercase">
                        {request.leave_type || "-"}
                      </span>
                    </td>

                    <td>
                      <small className="text-muted">
                        {formatDate(request.leave_from)}
                      </small>
                    </td>

                    <td>
                      <small className="text-muted">
                        {formatDate(request.leave_to)}
                      </small>
                    </td>

                    <td>
                      <span className="badge bg-info">
                        {request.total_days}
                      </span>
                    </td>

                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "200px" }}
                        title={request.reason}
                      >
                        {request.reason}
                      </div>
                    </td>

                    <td>
                      {request.attachment ? (
                        <a
                          href={`${REACT_APP_API_URL}${request.attachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted">No file</span>
                      )}
                    </td>

                    <td>{getStatusBadge(request.status) || "pending"}</td>

                    <td>
                      {request.status !== "pending" && request.remarks ? (
                        <div
                          className="text-truncate"
                          style={{ maxWidth: "250px" }}
                          title={request.remarks}
                        >
                          {request.remarks}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    <td>
                      <div className="d-flex flex-wrap gap-2">
                      {/* Delete Pending Leave (Only before leave start date) */}
                      {request.status === "pending" &&
                        new Date(request.leave_from) > new Date() && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteRequest(request.id)}
                            title="Delete Leave Request"
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        )}

                      {/* Request Cancellation */}
                      {request.status === "approved" &&
                        !request.cancellation_requested &&
                        new Date(request.leave_from) > new Date() && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              setSelectedLeaveId(request.id);
                              setCancelReason("");
                              setShowCancelModal(true);
                            }}
                            title="Request Cancellation of Approved Leave"
                          >
                            <i className="bi bi-calendar-x me-1"></i>
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>

                    <td>
                      {request.cancellation_requested ? (
                        <div
                          className="p-2 border rounded leave-cancellation-card"
                        >
                          {/* STATUS BADGE */}
                          <div className="mb-1">
                            {request.cancellation_status === "approved" && (
                              <span className="badge bg-success">Approved</span>
                            )}
                            {request.cancellation_status === "rejected" && (
                              <span className="badge bg-danger">Rejected</span>
                            )}
                            {request.cancellation_status === "pending" && (
                              <span className="badge bg-warning text-dark">Pending</span>
                            )}
                            {!request.cancellation_status && (
                              <span className="badge bg-secondary">Requested</span>
                            )}
                          </div>

                          {/* REQUEST DATE */}
                          {request.cancellation_requested_date && (
                            <div className="small text-muted">
                              <strong>Date:</strong>{" "}
                              {formatDate(request.cancellation_requested_date)}
                            </div>
                          )}

                          {/* REASON */}
                          {request.cancellation_reason && (
                            <div
                              className="small text-muted text-truncate"
                              title={request.cancellation_reason}
                            >
                              <strong>Reason:</strong> {request.cancellation_reason}
                            </div>
                          )}

                          {/* REJECTION REMARKS */}
                          {request.cancellation_status === "rejected" &&
                            request.cancellation_admin_remarks && (
                              <div
                                className="small text-danger text-truncate mt-1"
                                title={request.cancellation_admin_remarks}
                              >
                                <strong>Remarks:</strong>{" "}
                                {request.cancellation_admin_remarks}
                              </div>
                            )}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        )}
      </div>

      {showCancelModal && (
        <>
          <div className="modal show d-block leave-modal">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Request Leave Cancellation</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCancelModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label className="form-label">
                    Cancellation Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Enter valid reason for cancellation"
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowCancelModal(false)}
                    disabled={submittingCancel}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-warning"
                    onClick={submitCancellationRequest}
                    disabled={submittingCancel}
                  >
                    {submittingCancel ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}

    </div>
  );
};

export default LeaveRequestList;

